import { Observable } from 'rxjs/Observable';
import { Subscription } from 'rxjs/Subscription';
import 'rxjs/add/observable/forkJoin';
import _ from 'lodash';

import { SmartenitPlugin } from '../smartenit-plugin';
import { ILockedUnlocked } from '../../interfaces/locked-unlocked.interface';
import { IWebSocketDeviceMessage } from '../../websockets/websocket-device-message.interface';

const CACHE_TIME: number = 12 * 60 * 60;

export class DoorLockPlugin extends SmartenitPlugin implements ILockedUnlocked {
  private _lastUpdate: number = 0;

  public getReportableAttrs() {
    return ['LockState', 'DoorState', 'EnableLogging', 'Language', 'LEDSettings', 'AutoRelockTime',
      'SoundVolume', 'OperatingMode', 'DefaultConfigurationRegister', 'EnableLocalProgramming',
      'EnableOneTouchLocking', 'EnableInsideStatusLED', 'EnablePrivacyModeButton', 'WrongCodeEntryLimit', 'UserCodeTemporaryDisableTime',
      'SendPINOverTheAir', 'RequirePINforRFOperation', 'ZigBeeSecurityLevel', 'AlarmMask', 'KeypadOperationEventMask',
      'RFOperationEventMask', 'ManualOperationEventMask', 'RFIDOperationEventMask',
      'KeypadProgrammingEventMask', 'RFProgrammingEventMask', 'RFIDProgrammingEventMask'
    ];
  }

  public lock(): void {
    return this.executeMethod(
      this.componentId,
      this.processorName,
      'LockDoor',
      { Code: '' }
    );
  }

  public unlock(): void {
    return this.executeMethod(
      this.componentId,
      this.processorName,
      'UnlockDoor',
      { Code: '' }
    );
  }

  public getLastUpdateTimestamp(): number {
    return this._lastUpdate;
  }

  public isLocked(): boolean {
    return this.state === true;
  }

  public isUnlocked(): boolean {
    return this.state === false;
  }

  public getEffectOptions(): any {
    return [
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.EFFECTS_NL.LOCK',
          ''
        ),
        name: 'Lock',
        value: {
          method: 'LockDoor',
          payload: {
            Code: ''
          }
        }
      },
      {
        displayName: _.get(
          this.translationService.getResource(),
          'AR.EFFECTS_NL.UNLOCK',
          ''
        ),
        name: 'Unlock',
        value: {
          method: 'UnlockDoor',
          payload: {
            Code: ''
          }
        }
      }
    ];
  }

  public getEffectAttribute(): string | null {
    return null;
  }

  public getEffectMethod(context?: any): string | null {
    return null;
  }

  public getConditionOptions(): any {
    return [
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.IS_LOCKED',
            ''
          )
        ),
        name: 'is Locked',
        value: 1
      },
      {
        displayName: _.capitalize(
          _.get(
            this.translationService.getResource(),
            'AR.CONDITIONS_NL.IS_UNLOCKED',
            ''
          )
        ),
        name: 'is Unlocked',
        value: 2
      }
    ];
  }

  public getCachedValues(subscribe?: boolean): any {
    const cacheValues = [this.getCache('lastUpdate'), this.getCache()];

    const observable = Observable.forkJoin(cacheValues).flatMap((array) => {
      if (array && array[0] != null) {
        this._lastUpdate = array[0];
      }

      if (array && array[1] != null) {
        this.state = array[1];
      }

      if (
        this.state != null &&
        (this.state.constructor !== Object ||
          Object.keys(this.state).length > 0)
      ) {
        this.device.cached = true;
      }

      this._onUpdate.next({
        lastUpdate: this._lastUpdate,
        state: this.state
      });

      return Observable.of(this.state);
    });

    if (subscribe === true) {
      return observable;
    }

    const subscription: Subscription = observable.subscribe(() =>
      subscription.unsubscribe()
    );
  }

  public getConditionAttribute(): string {
    return 'LockState';
  }

  public getStatusPayload(): any {
    return ['LockState'];
  }

  public processMessage(
    message: IWebSocketDeviceMessage,
    timestamp?: number
  ): any {
    const attribute = message && message.attributeOrMethod;

    if (attribute === 'state' || attribute === 'LockState') {
      const response = message && message.data && message.data.response;

      this.state = parseInt(response.value) === 1;

      this._lastUpdate = parseInt(response.timestamp);

      this.setCache(
        'lastUpdate',
        this._lastUpdate,
        CACHE_TIME,
        undefined,
        timestamp
      );

      this._onUpdate.next({ state: this.state, lastUpdate: this._lastUpdate });
    }

    return this.state;
  }
}
